import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CERTIFICATE } from "@/constants/testIds";
import { Award, ArrowRight, Lock, Sparkles, CheckCircle2, Trophy, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import usePromoStatus from "@/lib/usePromoStatus";

export default function Certificates() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const tier = user?.tier || "free";
  const isPaid = tier === "basic" || tier === "premium" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [master, setMaster] = useState(null);
  const [issuing, setIssuing] = useState(false);

  const loadMaster = async () => {
    try {
      const r = await api.get(`/certificates/master/status?language=${i18n.language || "en"}`);
      setMaster(r.data);
    } catch { /* silently ignore — unauth users see funnel */ }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/certificates/me");
        if (!cancelled) setItems(r.data);
      } catch { /* fall through */ }
      if (!cancelled) await loadMaster();
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [i18n.language]);

  const claimMaster = async () => {
    setIssuing(true);
    try {
      const r = await api.post(`/certificates/master/issue?language=${i18n.language || "en"}`);
      toast.success(t("certificatesUi.master.claimSuccess"));
      // Refresh both lists so the new master cert shows up.
      const [meRes] = await Promise.all([api.get("/certificates/me"), loadMaster()]);
      setItems(meRes.data);
      // Navigate the user straight to their new diploma.
      window.location.assign(`/certificates/${r.data.code}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("certificatesUi.master.claimError"));
    } finally {
      setIssuing(false);
    }
  };

  const showFunnel = !isPaid && !loading && items.length === 0;
  const courseCerts = items.filter((c) => !c.master);
  const masterCert = items.find((c) => c.master) || master?.certificate || null;

  return (
    <div data-testid={CERTIFICATE.listRoot} className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-3">
          — {t("certificatesUi.pageTitle")}
        </div>
        <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
          {t("certificatesUi.listTitle")}
        </h1>
        <p className="mt-2 text-slate-600">{t("certificatesUi.listSubtitle")}</p>
      </div>

      {loading ? (
        <div className="text-slate-500">{t("certificatesUi.loading")}</div>
      ) : showFunnel ? (
        <CertificatesFunnel />
      ) : items.length === 0 ? (
        <>
          {master && (
            <MasterCertificateCard
              master={master}
              masterCert={masterCert}
              issuing={issuing}
              onClaim={claimMaster}
            />
          )}
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 text-blue-800 mb-4">
              <Award size={22} />
            </div>
            <p className="text-slate-600 max-w-md mx-auto">{t("certificatesUi.listEmpty")}</p>
          </div>
        </>
      ) : (
        <>
          {master && (
            <MasterCertificateCard
              master={master}
              masterCert={masterCert}
              issuing={issuing}
              onClaim={claimMaster}
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {courseCerts.map((c) => {
              const d = new Date(c.issued_at).toLocaleDateString(undefined, {
                year: "numeric", month: "short", day: "numeric",
              });
              return (
                <Link
                  key={c.id}
                  data-testid={CERTIFICATE.listCard}
                  to={`/certificates/${c.code}`}
                  className="group relative bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <div className="absolute top-4 right-4 text-blue-800 opacity-70 group-hover:opacity-100">
                    <ArrowRight size={16} />
                  </div>
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-amber-600 font-semibold mb-3">
                    <Award size={12} />
                    {t("certificatesUi.pageTitle")}
                  </div>
                  <h3 className="font-display font-bold text-xl text-slate-900 leading-snug pr-6">
                    {c.course_title}
                  </h3>
                  <div className="mt-3 text-sm text-slate-600">{c.user_name}</div>
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                    <span>{d}</span>
                    <span className="font-mono">{c.code}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function MasterCertificateCard({ master, masterCert, issuing, onClaim }) {
  const { t } = useTranslation();
  const completed = master?.completed?.length || 0;
  const required = master?.required_count || 3;
  const pct = Math.min(100, Math.round((completed / required) * 100));
  const isIssued = Boolean(masterCert);

  // Build the ordered course roster: completed rows first, then missing.
  const rows = [
    ...(master?.completed || []).map((c) => ({ ...c, done: true })),
    ...(master?.missing || []).map((c) => ({ ...c, done: false })),
  ];

  return (
    <div
      data-testid={CERTIFICATE.masterCard}
      className="relative overflow-hidden rounded-2xl border border-amber-500/40 bg-slate-950 text-white p-8 sm:p-10"
    >
      <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />

      <div className="relative grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-4">
            <Crown size={14} />
            {t("certificatesUi.master.eyebrow")}
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight">
            {t("certificatesUi.master.title")}
          </h2>
          <p className="mt-4 text-slate-300 max-w-lg leading-relaxed text-sm">
            {t("certificatesUi.master.subtitle")}
          </p>

          <div
            data-testid={CERTIFICATE.masterProgress}
            className="mt-6 max-w-md"
          >
            <div className="flex items-center justify-between text-xs text-slate-300 mb-2">
              <span className="font-semibold text-amber-300">
                {t("certificatesUi.master.progressLabel", { completed, required })}
              </span>
              <span className="font-mono text-slate-400">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  isIssued
                    ? "bg-emerald-500"
                    : "bg-gradient-to-r from-blue-500 to-amber-400"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          <div className="mt-6">
            {isIssued ? (
              <Link
                to={`/certificates/${masterCert.code}`}
                data-testid={CERTIFICATE.masterViewButton}
              >
                <Button
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-12 px-8"
                >
                  <Trophy size={16} className="mr-2" />
                  {t("certificatesUi.master.viewDiploma")}
                </Button>
              </Link>
            ) : master?.eligible ? (
              <Button
                data-testid={CERTIFICATE.masterIssueButton}
                onClick={onClaim}
                disabled={issuing}
                size="lg"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8 disabled:opacity-60"
              >
                <Sparkles size={16} className="mr-2" />
                {issuing ? t("certificatesUi.master.claiming") : t("certificatesUi.master.claim")}
              </Button>
            ) : (
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-slate-400 font-semibold">
                <Lock size={12} />
                {t("certificatesUi.master.locked", { count: master?.missing?.length || 0 })}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl bg-white/5 border border-white/10 p-5 backdrop-blur-sm">
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold mb-4">
            {t("certificatesUi.master.courseListHeading")}
          </div>
          <ul className="space-y-3">
            {rows.map((r) => {
              const upgradeable = !r.done && r.action === "upgrade";
              return (
                <li
                  key={r.slug}
                  className="flex items-start gap-3 text-sm text-slate-100"
                >
                  <span
                    className={`mt-0.5 inline-flex w-5 h-5 rounded-full items-center justify-center shrink-0 ${
                      r.done
                        ? "bg-emerald-500 text-slate-950"
                        : upgradeable
                        ? "bg-amber-400/90 text-slate-950"
                        : "bg-white/10 text-slate-400"
                    }`}
                  >
                    {r.done
                      ? <CheckCircle2 size={14} />
                      : upgradeable
                      ? <Sparkles size={11} />
                      : <Lock size={11} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={r.done ? "font-semibold" : "font-medium text-slate-300"}>
                      {r.course_title}
                    </div>
                    {r.course_level && (
                      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
                        {r.course_level}
                      </div>
                    )}
                    {upgradeable && r.course_id && (
                      <Link
                        to={`/courses/${r.course_id}`}
                        data-testid={`master-upgrade-link-${r.slug}`}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 hover:text-amber-200 transition-colors"
                      >
                        {t("certificatesUi.master.upgradeAction")}
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function CertificatesFunnel() {
  const { t } = useTranslation();
  const { params: P } = usePromoStatus();
  return (
    <div className="space-y-8" data-testid="certificates-funnel">
      {/* Mock certificate preview — what one looks like */}
      <div
        data-testid="certificates-funnel-preview"
        className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden"
      >
        <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
          <Lock size={14} />
        </div>
        <div className="p-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-center">
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="w-32 h-32 rounded-full border-8 border-amber-500 flex items-center justify-center bg-gradient-to-br from-amber-50 to-white shadow-lg">
                <Award size={44} className="text-amber-600" />
              </div>
              <div className="absolute -bottom-2 -right-2 bg-blue-900 text-white text-[10px] font-bold uppercase tracking-widest rounded px-2 py-1">
                {t("certificatesUi.funnel.lockedBadge")}
              </div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-600 font-semibold mb-2">
              {t("certificatesUi.funnel.previewEyebrow")}
            </div>
            <h3 className="font-display font-bold text-2xl text-slate-900 tracking-tight leading-tight">
              {t("certificatesUi.funnel.previewTitle")}
            </h3>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-lg">
              {t("certificatesUi.funnel.previewDescription")}
            </p>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              {[
                t("certificatesUi.funnel.feature1"),
                t("certificatesUi.funnel.feature2"),
                t("certificatesUi.funnel.feature3"),
                t("certificatesUi.funnel.feature4"),
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-blue-800 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Upgrade CTA */}
      <div
        data-testid="certificates-funnel-cta"
        className="relative overflow-hidden bg-slate-950 text-white rounded-2xl border border-amber-500/25 p-10"
      >
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="relative grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-4">
              <Sparkles size={14} /> {t("certificatesUi.funnel.ctaEyebrow")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight">
              {t("certificatesUi.funnel.ctaTitle")}
            </h2>
            <p className="mt-4 text-slate-300 max-w-lg leading-relaxed text-sm">
              {t("certificatesUi.funnel.ctaDescription")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing" data-testid="certificates-funnel-pricing-cta">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8">
                  {t("certificatesUi.funnel.seeMemberships")} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/courses" data-testid="certificates-funnel-courses-cta">
                <Button size="lg" variant="outline" className="border-white/20 text-white bg-transparent hover:bg-white/10 hover:border-white h-12 px-8">
                  {t("certificatesUi.funnel.keepBrowsing")}
                </Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {[
              { labelKey: "certificatesUi.funnel.basicLabel", price: P.basic, noteKey: "certificatesUi.funnel.basicNote" },
              { labelKey: "certificatesUi.funnel.premiumLabel", price: P.premium, noteKey: "certificatesUi.funnel.premiumNote" },
            ].map((tier) => (
              <div key={tier.labelKey} className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-semibold">
                    {t(tier.labelKey)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400 leading-snug">{t(tier.noteKey)}</div>
                </div>
                <div className="font-display font-black text-2xl text-white tracking-tight shrink-0">
                  {tier.price}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
